// ============================================================================
// ASSESSMENT PAGE - Coursiv-Inspired Redesign
// Clean, spacious, encouraging design with social proof
// ============================================================================

import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Check, Sparkles, Quote, Users, Clock, Shield, Star, Zap } from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';

// ============================================================================
// RISK DATA - Research-based scoring
// ============================================================================

const INDUSTRY_RISK = {
  "technology_it": 58,
  "healthcare": 28,
  "finance_banking": 65,
  "education": 42,
  "retail": 55,
  "manufacturing": 62,
  "creative_design": 48,
  "professional_services": 52,
  "hospitality": 38,
  "real_estate": 35,
  "government": 32,
  "other": 45
};

const PROFESSION_RISK = {
  "executive": 25,
  "management": 35,
  "skilled_trades": 28,
  "healthcare_clinical": 30,
  "education_training": 40,
  "technical_engineering": 45,
  "legal": 48,
  "operations": 50,
  "hr_people": 52,
  "creative_design": 55,
  "sales": 55,
  "research_analysis": 58,
  "marketing": 62,
  "finance_accounting": 65,
  "customer_service": 72,
  "administrative": 78,
  "other": 50
};

// ============================================================================
// SCORING ENGINE
// ============================================================================

function calculateScore(answers) {
  const industry = answers.S2 || "other";
  const profession = answers.S4 || "other";
  
  const industryRaw = INDUSTRY_RISK[industry] || 45;
  const professionRaw = PROFESSION_RISK[profession] || 50;
  const industryScore = Math.round((industryRaw * 0.4) + (professionRaw * 0.6));
  
  let knowledgePoints = 0, knowledgeMax = 0;
  const knowledgeQs = ['S3', 'S5'];
  knowledgeQs.forEach(qId => {
    knowledgeMax += 4;
    const ans = answers[qId];
    if (ans) {
      const pointMap = { 
        'none': 0, 'basic': 1, 'moderate': 2, 'good': 3, 'expert': 4,
        'never': 0, 'rarely': 1, 'sometimes': 2, 'regularly': 3, 'frequently': 4
      };
      knowledgePoints += pointMap[ans] || 0;
    }
  });
  const knowledgeScore = Math.round((knowledgePoints / knowledgeMax) * 100);
  
  let riskPoints = 0, riskMax = 0;
  const riskQs = ['E2', 'E3'];
  riskQs.forEach(qId => {
    riskMax += 5;
    const ans = answers[qId];
    if (ans) {
      const pointMap = { 
        'creative': 1, 'mixed': 2, 'standard': 3, 'routine': 4, 'data': 5,
        'low': 1, 'medium_low': 2, 'medium': 3, 'medium_high': 4, 'high': 5
      };
      riskPoints += pointMap[ans] || 0;
    }
  });
  const riskScore = Math.round((riskPoints / riskMax) * 100);
  
  let readinessPoints = 0, readinessMax = 0;
  const readinessQs = ['E8', 'S7', 'E10'];
  readinessQs.forEach(qId => {
    readinessMax += 5;
    const ans = answers[qId];
    if (ans) {
      const pointMap = { 
        'very': 5, 'well': 4, 'moderate': 3, 'slightly': 2, 'not': 1,
        'continuous': 5, 'active': 4, 'self': 3, 'convenient': 2, 'required': 1,
        'roadmap': 5, 'enrolled': 4, 'planning': 3, 'thinking': 2, 'none': 1
      };
      readinessPoints += pointMap[ans] || 0;
    }
  });
  const readinessScore = Math.round((readinessPoints / readinessMax) * 100);
  
  const compositeScore = Math.round(
    (industryScore * 0.25) + 
    (riskScore * 0.35) + 
    ((100 - knowledgeScore) * 0.20) + 
    ((100 - readinessScore) * 0.20)
  );
  
  let riskLevel = "MODERATE";
  if (compositeScore >= 70) riskLevel = "CRITICAL";
  else if (compositeScore >= 55) riskLevel = "HIGH";
  else if (compositeScore >= 40) riskLevel = "MODERATE";
  else if (compositeScore >= 25) riskLevel = "LOW";
  else riskLevel = "MINIMAL";
  
  let timeHorizon = "MEDIUM_TERM";
  if (compositeScore >= 70) timeHorizon = "IMMEDIATE";
  else if (compositeScore >= 55) timeHorizon = "SHORT_TERM";
  else if (compositeScore >= 40) timeHorizon = "MEDIUM_TERM";
  else timeHorizon = "LONG_TERM";
  
  const keyRiskFactors = [];
  if (industryScore >= 60) keyRiskFactors.push("Your industry and role have high AI automation potential");
  if (riskScore >= 60) keyRiskFactors.push("Your daily tasks are highly automatable");
  if (knowledgeScore < 40) keyRiskFactors.push("Limited AI knowledge increases vulnerability");
  if (readinessScore < 40) keyRiskFactors.push("Low preparedness for AI-driven changes");
  
  const protectiveFactors = [];
  if (knowledgeScore >= 60) protectiveFactors.push("Strong AI knowledge foundation");
  if (readinessScore >= 60) protectiveFactors.push("Proactive approach to skill development");
  if (industryScore < 40) protectiveFactors.push("Industry and role have lower automation risk");
  
  const recommendations = [
    "Stay updated on AI developments in your industry",
    "Build hands-on experience with AI tools relevant to your work"
  ];
  if (knowledgeScore < 50) recommendations.unshift("Start with foundational AI courses on Coursera or LinkedIn Learning");
  if (readinessScore < 50) recommendations.unshift("Create a 90-day AI learning plan with specific milestones");
  
  return {
    compositeScore,
    riskLevel,
    timeHorizon,
    industryScore,
    knowledgeScore,
    riskScore,
    readinessScore,
    keyRiskFactors,
    protectiveFactors,
    recommendations,
    confidence: 0.85,
    userType: answers.S1 || "employee"
  };
}

// ============================================================================
// AVATAR STACK COMPONENT
// ============================================================================

function AvatarStack() {
  return (
    <div className="flex items-center justify-center gap-3">
      <div className="flex -space-x-3">
        {[
          "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=40&h=40&fit=crop&crop=face",
          "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop&crop=face",
          "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=40&h=40&fit=crop&crop=face",
          "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face",
        ].map((src, i) => (
          <div
            key={i}
            className="w-10 h-10 rounded-full border-2 border-white bg-gray-200 overflow-hidden"
          >
            <img src={src} alt="" className="w-full h-full object-cover" />
          </div>
        ))}
        <div className="w-10 h-10 rounded-full border-2 border-white bg-teal-500 flex items-center justify-center text-white text-xs font-bold">
          +9k
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// STEP INDICATOR COMPONENT
// ============================================================================

function StepIndicator({ currentStep, totalSteps }) {
  return (
    <div className="flex items-center justify-center gap-1.5">
      {Array.from({ length: totalSteps }, (_, i) => (
        <div
          key={i}
          className={`h-1.5 rounded-full transition-all duration-300 ${
            i < currentStep 
              ? 'w-6 bg-teal-500' 
              : i === currentStep 
                ? 'w-6 bg-teal-500' 
                : 'w-1.5 bg-gray-300'
          }`}
        />
      ))}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function Assessment() {
  const { t, i18n } = useTranslation('assessment');
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showIntro, setShowIntro] = useState(true);
  const [showMidpoint, setShowMidpoint] = useState(false);
  
  const isRTL = i18n.dir() === 'rtl';

  const questions = useMemo(() => [
    {
      id: "S1",
      section: t('sections.yourProfile'),
      question: t('questions.S1.question'),
      type: "single",
      options: [
        { text: t('questions.S1.options.employee'), value: "employee", icon: "💼" },
        { text: t('questions.S1.options.businessOwner'), value: "business_owner", icon: "🏢" },
        { text: t('questions.S1.options.student'), value: "student", icon: "🎓" }
      ]
    },
    {
      id: "S2",
      section: t('sections.yourProfile'),
      question: t('questions.S2.question'),
      type: "single",
      options: [
        { text: t('questions.S2.options.technology'), value: "technology_it", icon: "💻" },
        { text: t('questions.S2.options.healthcare'), value: "healthcare", icon: "🏥" },
        { text: t('questions.S2.options.finance'), value: "finance_banking", icon: "🏦" },
        { text: t('questions.S2.options.education'), value: "education", icon: "📚" },
        { text: t('questions.S2.options.retail'), value: "retail", icon: "🛒" },
        { text: t('questions.S2.options.manufacturing'), value: "manufacturing", icon: "🏭" },
        { text: t('questions.S2.options.creative'), value: "creative_design", icon: "🎨" },
        { text: t('questions.S2.options.professional'), value: "professional_services", icon: "👔" },
        { text: t('questions.S2.options.hospitality'), value: "hospitality", icon: "🏨" },
        { text: t('questions.S2.options.realEstate'), value: "real_estate", icon: "🏠" },
        { text: t('questions.S2.options.government'), value: "government", icon: "🏛️" },
        { text: t('questions.S2.options.other'), value: "other", icon: "📦" }
      ]
    },
    {
      id: "S4",
      section: t('sections.yourProfile'),
      question: t('questions.S4.question'),
      type: "single",
      options: [
        { text: t('questions.S4.options.executive'), value: "executive" },
        { text: t('questions.S4.options.management'), value: "management" },
        { text: t('questions.S4.options.sales'), value: "sales" },
        { text: t('questions.S4.options.marketing'), value: "marketing" },
        { text: t('questions.S4.options.finance_accounting'), value: "finance_accounting" },
        { text: t('questions.S4.options.hr_people'), value: "hr_people" },
        { text: t('questions.S4.options.operations'), value: "operations" },
        { text: t('questions.S4.options.customer_service'), value: "customer_service" },
        { text: t('questions.S4.options.technical_engineering'), value: "technical_engineering" },
        { text: t('questions.S4.options.research_analysis'), value: "research_analysis" },
        { text: t('questions.S4.options.creative_design'), value: "creative_design" },
        { text: t('questions.S4.options.administrative'), value: "administrative" },
        { text: t('questions.S4.options.healthcare_clinical'), value: "healthcare_clinical" },
        { text: t('questions.S4.options.education_training'), value: "education_training" },
        { text: t('questions.S4.options.legal'), value: "legal" },
        { text: t('questions.S4.options.skilled_trades'), value: "skilled_trades" },
        { text: t('questions.S4.options.other'), value: "other" }
      ]
    },
    {
      id: "S3",
      section: t('sections.aiFamiliarity'),
      question: t('questions.S3.question'),
      type: "single",
      options: [
        { text: t('questions.S3.options.none'), value: "none" },
        { text: t('questions.S3.options.basic'), value: "basic" },
        { text: t('questions.S3.options.moderate'), value: "moderate" },
        { text: t('questions.S3.options.good'), value: "good" },
        { text: t('questions.S3.options.expert'), value: "expert" }
      ]
    },
    {
      id: "S5",
      section: t('sections.aiFamiliarity'),
      question: t('questions.S5.question'),
      type: "single",
      options: [
        { text: t('questions.S5.options.never'), value: "never" },
        { text: t('questions.S5.options.rarely'), value: "rarely" },
        { text: t('questions.S5.options.sometimes'), value: "sometimes" },
        { text: t('questions.S5.options.regularly'), value: "regularly" },
        { text: t('questions.S5.options.frequently'), value: "frequently" }
      ]
    },
    {
      id: "E2",
      section: t('sections.riskAssessment'),
      question: t('questions.E2.question'),
      type: "single",
      options: [
        { text: t('questions.E2.options.creative'), value: "creative" },
        { text: t('questions.E2.options.mixed'), value: "mixed" },
        { text: t('questions.E2.options.standard'), value: "standard" },
        { text: t('questions.E2.options.routine'), value: "routine" },
        { text: t('questions.E2.options.data'), value: "data" }
      ]
    },
    {
      id: "E3",
      section: t('sections.riskAssessment'),
      question: t('questions.E3.question'),
      type: "single",
      options: [
        { text: t('questions.E3.options.low'), value: "low" },
        { text: t('questions.E3.options.mediumLow'), value: "medium_low" },
        { text: t('questions.E3.options.medium'), value: "medium" },
        { text: t('questions.E3.options.mediumHigh'), value: "medium_high" },
        { text: t('questions.E3.options.high'), value: "high" }
      ]
    },
    {
      id: "E8",
      section: t('sections.futureReadiness'),
      question: t('questions.E8.question'),
      type: "single",
      options: [
        { text: t('questions.E8.options.not'), value: "not" },
        { text: t('questions.E8.options.slightly'), value: "slightly" },
        { text: t('questions.E8.options.moderate'), value: "moderate" },
        { text: t('questions.E8.options.well'), value: "well" },
        { text: t('questions.E8.options.very'), value: "very" }
      ]
    },
    {
      id: "S7",
      section: t('sections.futureReadiness'),
      question: t('questions.S7.question'),
      type: "single",
      options: [
        { text: t('questions.S7.options.required'), value: "required" },
        { text: t('questions.S7.options.convenient'), value: "convenient" },
        { text: t('questions.S7.options.self'), value: "self" },
        { text: t('questions.S7.options.active'), value: "active" },
        { text: t('questions.S7.options.continuous'), value: "continuous" }
      ]
    },
    {
      id: "E10",
      section: t('sections.futureReadiness'),
      question: t('questions.E10.question'),
      type: "single",
      options: [
        { text: t('questions.E10.options.none'), value: "none" },
        { text: t('questions.E10.options.thinking'), value: "thinking" },
        { text: t('questions.E10.options.planning'), value: "planning" },
        { text: t('questions.E10.options.enrolled'), value: "enrolled" },
        { text: t('questions.E10.options.roadmap'), value: "roadmap" }
      ]
    }
  ], [t, i18n.language]);

  const currentQ = questions[currentIndex];
  const totalQuestions = questions.length;
  const progress = Math.round(((currentIndex + 1) / totalQuestions) * 100);
  
  const isAnswered = currentQ ? answers[currentQ.id] !== undefined : false;
  
  const selectOption = useCallback((value) => {
    setAnswers(prev => ({
      ...prev,
      [currentQ.id]: value
    }));
  }, [currentQ?.id]);
  
  const startAssessment = () => {
    setShowIntro(false);
  };

  const continuePastMidpoint = () => {
    setShowMidpoint(false);
    setCurrentIndex(4);
  };
  
  const goNext = () => {
    if (currentIndex === 3 && !showMidpoint) {
      setShowMidpoint(true);
      return;
    }
    
    if (currentIndex < totalQuestions - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      const results = calculateScore(answers);
      localStorage.removeItem('lab2040_assessment_saved');
      localStorage.setItem('lab2040_assessment_results', JSON.stringify(results));
      localStorage.setItem('lab2040_assessment_answers', JSON.stringify(answers));
      navigate('/results');
    }
  };
  
  const goPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else {
      setShowIntro(true);
    }
  };

  // ===========================================================================
  // INTRO SCREEN - Coursiv-inspired
  // ===========================================================================
  if (showIntro) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Minimal Header */}
        <header className="pt-6 px-4">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <Link to="/" className="font-serif text-2xl font-bold">
              <span className="text-gray-900">Lab</span>
              <span className="text-teal-500">2040</span>
            </Link>
            <LanguageSelector compact />
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-8">
          {/* Social Proof */}
          <div className="text-center mb-8">
            <AvatarStack />
            <p className="text-gray-600 mt-3 text-sm">
              <span className="font-semibold text-gray-900">10,000+</span> professionals have assessed their AI readiness
            </p>
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl shadow-gray-200/50 text-center">
            {/* Icon */}
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-2xl mb-8 shadow-lg shadow-teal-200">
              <Sparkles className="w-10 h-10 text-white" />
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
              {t('intro.title', 'Discover Your AI Readiness')}
            </h1>

            {/* Subtext */}
            <p className="text-gray-500 text-lg mb-8 max-w-md mx-auto">
              {t('intro.subtext', 'No right or wrong answers. Takes less than 90 seconds.')}
            </p>

            {/* Features */}
            <div className="flex flex-wrap justify-center gap-4 mb-10">
              <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 px-4 py-2 rounded-full">
                <Clock className="w-4 h-4 text-teal-500" />
                <span>90 seconds</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 px-4 py-2 rounded-full">
                <Shield className="w-4 h-4 text-teal-500" />
                <span>100% private</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 px-4 py-2 rounded-full">
                <Zap className="w-4 h-4 text-teal-500" />
                <span>Instant results</span>
              </div>
            </div>

            {/* CTA Button */}
            <button
              onClick={startAssessment}
              className="w-full sm:w-auto px-10 py-4 bg-gradient-to-r from-gray-900 to-gray-800 text-white font-bold text-lg rounded-full hover:shadow-xl hover:shadow-gray-300 transition-all duration-300 flex items-center justify-center gap-3 mx-auto group"
            >
              {t('intro.cta', 'Start Free Assessment')}
              <ChevronRight className={`w-5 h-5 group-hover:translate-x-1 transition-transform ${isRTL ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {/* Testimonial */}
          <div className="mt-10 text-center">
            <div className="inline-block bg-white rounded-2xl p-6 shadow-lg shadow-gray-100 max-w-lg">
              <div className="flex gap-1 justify-center mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-gray-600 italic mb-3">
                {t('intro.testimonial.quote', '"This helped me understand where I stand with AI and gave me a clear action plan."')}
              </p>
              <p className="text-teal-600 font-semibold text-sm">
                {t('intro.testimonial.author', '— Sarah, Operations Manager')}
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ===========================================================================
  // MIDPOINT SCREEN - Celebration
  // ===========================================================================
  if (showMidpoint) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Minimal Header */}
        <header className="pt-6 px-4">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <Link to="/" className="font-serif text-2xl font-bold">
              <span className="text-gray-900">Lab</span>
              <span className="text-teal-500">2040</span>
            </Link>
            <LanguageSelector compact />
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-12">
          {/* Progress */}
          <div className="mb-8">
            <StepIndicator currentStep={4} totalSteps={10} />
          </div>

          {/* Celebration Card */}
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl shadow-gray-200/50 text-center">
            {/* Animated Check */}
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full mb-8 shadow-lg shadow-green-200 animate-bounce">
              <Check className="w-10 h-10 text-white" />
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('midpoint.title', 'You\'re doing great! 🎉')}
            </h1>

            {/* Progress Text */}
            <p className="text-gray-500 text-lg mb-8">
              40% complete — just a few more questions to go!
            </p>

            {/* Testimonial */}
            <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-2xl p-6 mb-8 max-w-md mx-auto">
              <div className="flex gap-1 justify-center mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-gray-600 italic mb-3">
                {t('midpoint.testimonial.quote', '"It helped me see which parts of my role might change — without scaring me."')}
              </p>
              <p className="text-teal-600 font-semibold text-sm">
                {t('midpoint.testimonial.author', '— Daniel, Business Analyst')}
              </p>
            </div>

            {/* CTA Button */}
            <button
              onClick={continuePastMidpoint}
              className="w-full sm:w-auto px-10 py-4 bg-gradient-to-r from-gray-900 to-gray-800 text-white font-bold text-lg rounded-full hover:shadow-xl hover:shadow-gray-300 transition-all duration-300 flex items-center justify-center gap-3 mx-auto group"
            >
              {t('midpoint.cta', 'Continue')}
              <ChevronRight className={`w-5 h-5 group-hover:translate-x-1 transition-transform ${isRTL ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </main>
      </div>
    );
  }

  // ===========================================================================
  // QUESTIONS SCREEN - Clean & Spacious
  // ===========================================================================
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Minimal Header */}
      <header className="pt-6 px-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-serif text-2xl font-bold">
            <span className="text-gray-900">Lab</span>
            <span className="text-teal-500">2040</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500 font-medium">
              {currentIndex + 1}/{totalQuestions}
            </span>
            <LanguageSelector compact />
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Step Indicator */}
        <div className="mb-8">
          <StepIndicator currentStep={currentIndex} totalSteps={totalQuestions} />
        </div>

        {/* Question Card */}
        <div className="bg-white rounded-3xl p-8 md:p-10 shadow-xl shadow-gray-200/50 mb-6">
          {/* Section Badge */}
          <div className="mb-6">
            <span className="text-xs font-bold text-teal-600 bg-teal-50 px-4 py-1.5 rounded-full uppercase tracking-wider">
              {currentQ.section}
            </span>
          </div>

          {/* Question */}
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 leading-snug">
            {currentQ.question}
          </h2>

          {/* Options */}
          <div className="space-y-3">
            {currentQ.options.map((option) => {
              const isSelected = answers[currentQ.id] === option.value;
              
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => selectOption(option.value)}
                  className={`w-full text-start p-5 rounded-2xl border-2 transition-all duration-200 flex items-center gap-4 hover:border-teal-300 hover:bg-teal-50/50 ${
                    isSelected
                      ? 'border-teal-500 bg-teal-50 shadow-md shadow-teal-100'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  {/* Emoji Icon (if exists) */}
                  {option.icon && (
                    <span className="text-2xl">{option.icon}</span>
                  )}
                  
                  {/* Radio Circle */}
                  <div
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                      isSelected
                        ? 'border-teal-500 bg-teal-500'
                        : 'border-gray-300 bg-white'
                    }`}
                  >
                    {isSelected && <Check className="w-4 h-4 text-white" />}
                  </div>
                  
                  {/* Text */}
                  <span className={`font-medium text-lg ${isSelected ? 'text-teal-700' : 'text-gray-700'}`}>
                    {option.text}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <button
            type="button"
            onClick={goPrevious}
            className="text-gray-500 hover:text-teal-600 font-medium transition-colors px-4 py-2"
          >
            ← {t('nav.back', 'Back')}
          </button>

          <button
            type="button"
            onClick={goNext}
            disabled={!isAnswered}
            className={`px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 flex items-center gap-2 group ${
              isAnswered
                ? 'bg-gradient-to-r from-gray-900 to-gray-800 text-white hover:shadow-xl hover:shadow-gray-300'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {currentIndex === totalQuestions - 1 ? t('nav.seeResults', 'See My Results') : t('nav.next', 'Continue')}
            <ChevronRight className={`w-5 h-5 ${isAnswered ? 'group-hover:translate-x-1' : ''} transition-transform ${isRTL ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Reassurance */}
        <p className="text-center text-gray-400 text-sm mt-8">
          🔒 Your answers are private and never shared
        </p>
      </main>
    </div>
  );
}
